import type { ModelAdapter } from "./model";

/**
 * FakeModel — a frequency-biased uniform distribution.
 *
 * Used until a real LM is integrated. Produces a probability distribution
 * over the 256 possible next bytes based on the recent-byte histogram,
 * mixed with a uniform floor. With this, the arithmetic coder will
 * achieve mild but non-trivial compression on text-heavy input
 * (~1.3-1.8x on natural language) — enough to make the bandwidth HUD
 * tick visibly, without lying about doing real LM compression.
 */
export class FakeModel implements ModelAdapter {
  readonly contextWindow = 64;

  /** How much weight the histogram gets vs. uniform. 0 = pure uniform, 1 = pure histogram. */
  private readonly bias: number;

  constructor(bias = 0.6) {
    this.bias = Math.max(0, Math.min(1, bias));
  }

  async getLogits(context: Uint8Array): Promise<Float32Array> {
    const probs = new Float32Array(256);
    const uniform = 1 / 256;

    if (context.length === 0) {
      probs.fill(uniform);
      return probs;
    }

    // Histogram of the recent context window.
    const hist = new Uint32Array(256);
    for (let i = 0; i < context.length; i++) hist[context[i]]++;

    // Laplace-smoothed empirical distribution.
    const total = context.length + 256;
    for (let i = 0; i < 256; i++) {
      const empirical = (hist[i] + 1) / total;
      probs[i] = this.bias * empirical + (1 - this.bias) * uniform;
    }

    // Re-normalize defensively (clamp out floating point drift).
    let sum = 0;
    for (let i = 0; i < 256; i++) sum += probs[i];
    for (let i = 0; i < 256; i++) probs[i] /= sum;

    return probs;
  }
}
